import { attr, belongsTo, hasMany } from "@ember-data/model";
import { assert } from "@ember/debug";
import { service } from "@ember/service";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type FlashMessagesService from "ember-cli-flash/services/flash-messages";
import { task } from "ember-concurrency";
import type Store from "ember-data/store";
import type IntlService from "ember-intl/services/intl";
import type AlertOngoingJournalEvent from "parog-web/models/alert-ongoing-journal-event";
import type AlertProcessedJournalEvent from "parog-web/models/alert-processed-journal-event";
import type CommentWrittenJournalEvent from "parog-web/models/comment-written-journal-event";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type ParogService from "parog-web/services/parog";
import type SemeiaSessionService from "parog-web/services/session";
import {
  isJourneyForgetAlert,
  isMrcStageChangealert,
  isOfComputedType,
  isTlsPrescriptionAlert,
  tlsPrescriptionTrigger
} from "parog-web/utils/alert";
import tWithDefault from "parog-web/utils/t-with-default";
import type MrcStageEvaluatedJournalEvent from "./mrc-stage-evaluated-journal-event";

type FormattedDate = `${string}-${string}-${string}`;

export type TlsPrescriptionTrigger = {
  type: "expiration";
  nextPrescriptionStartDate: FormattedDate;
  nextPrescriptionEndDate: FormattedDate;
  healthCentreId?: string;
  prescriptionType?: "renewal" | "initial";
  patient?: {
    firstName: string;
    lastName: string;
    referentMedicId: string;
    followUpReason: string;
  };
};

export type PrescriptionTrigger = {
  type: "expiration";
  endOfValidity: FormattedDate;
};

/** A ComputedAlertType is based on the combination of several alert's attributes */
export type ComputedAlertType =
  | "tls-prescription"
  | "bio-exam-prescription"
  | "medication-prescription"
  | "mici-survey"
  | "treatment-compliance";

//---- Automatically generated, don't update this part ----

type AlertTypeStringEnum =
  | "biological"
  | "clinical"
  | "journey"
  | "non_calculable"
  | "mrc-stage-change-4"
  | "mrc-stage-change-5"
  | "treatment-compliance";

type CriticalityStringEnum = "high";

export default class AlertTriggeredJournalEvent extends JournalEvent {
  static override type = "alert-triggered-journal-event";

  @attr("string") declare alertType: AlertTypeStringEnum;

  @attr("string") declare subType: string;

  @attr("string") declare source: string;

  /**
   * At the moment, let's fetch the localized description of the alert
   * triggered JE from the API. If, at some point, we manage to compute this
   * descritpion on the front side, then we will remove this attr.
   *
   * FIXME: this is a legacy attribute, maybe not used anymore. We may remove
   * it.
   */
  @attr("string") declare frLocalizedDescription: string;

  /**
   * Trigger - A "Trigger" is an object which describes why the rule has been
   * triggered.
   *
   * He must provides two keys :
   * @attr [string] key
   * @attr [string] type
   *
   * @example Create an OutOfRangeTrigger
   * {
   *   key: "blood-pressure-pas",
   *   type: "out-of-range",
   *   min: 110,
   *   max: 160,
   *   value: 100
   * }
   */
  @attr({
    defaultValue() {
      return [];
    }
  })
  declare triggers: any[];

  @attr("string") declare reasonKey: string;

  /**
   * criticality of the alert;
   */
  @attr("string") declare criticality: CriticalityStringEnum;

  @attr("string") declare triggeringJournalEventId: string;

  @attr("string") declare triggeringJournalEventType: string;

  @attr("date") declare triggeringJournalEventOccurredAt: Date;

  @belongsTo("alert-processed-journal-event", {
    async: false,
    inverse: "alertTriggeredJournalEvent"
  })
  declare alertProcessedJournalEvent: AlertProcessedJournalEvent;

  @belongsTo("alert-ongoing-journal-event", {
    async: false,
    inverse: "alertTriggeredJournalEvent"
  })
  declare alertOngoingJournalEvent: AlertOngoingJournalEvent;

  @belongsTo("mrc-stage-evaluated-journal-event", {
    async: false,
    inverse: "alertTriggeredJournalEvent"
  })
  declare mrcStageEvaluatedJournalEvent: MrcStageEvaluatedJournalEvent;

  static override validations(intl: IntlService) {
    return {
      alertType: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.alert-triggered-journal-event.alert-type"
          )
        }),
        validateInclusion({
          list: [
            "biological",
            "clinical",
            "journey",
            "non_calculable",
            "mrc-stage-change-4",
            "mrc-stage-change-5",
            "treatment-compliance"
          ],
          description: intl.t(
            "validations.alert-triggered-journal-event.alert-type"
          )
        })
      ],
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.alert-triggered-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      criticality: validateInclusion({
        list: ["high"],
        allowBlank: true,
        description: intl.t(
          "validations.alert-triggered-journal-event.criticality"
        )
      })
    };
  }

  //---- End of generated code ----

  @service declare flashMessages: FlashMessagesService;
  @service declare intl: IntlService;
  @service declare parog: ParogService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  @belongsTo("journal-event", {
    async: false,
    inverse: "alertTriggeredJournalEvents",
    polymorphic: true,
    as: "journal-event"
  })
  declare triggeringJournalEvent?: JournalEvent;

  @belongsTo("tls-prescribed-journal-event", {
    async: false,
    inverse: "alertTriggeredJournalEvent"
  })
  declare tlsPrescribedJournalEvent: AlertTriggeredJournalEvent;

  @belongsTo("patient", {
    async: false,
    inverse: "alertTriggeredJournalEvents"
  })
  declare patient: Patient;

  @hasMany("comment-written-journal-event", {
    async: false,
    inverse: "originJournalEvent",
    as: "journal-event",
    polymorphic: true
  })
  declare commentWrittenJournalEvents: CommentWrittenJournalEvent[];

  /**
   * the alert object that come from the payload from the Alertor
   */
  @attr() declare rawOmop: {
    person_id?: string;
    trigger_date?: string;
    trigger_dates: string[];
    trigger_id?: string;
    trigger_ids: string[];
    message?: string;
    message_without_value: string;
    isolated_value: string;
    isolated_values: string[];
    category: string;
    variable: string;
    alert_rule_id?: string;
    criticality?: "high";
  };

  get hasHistory(): boolean {
    const firstTrigger = this.triggers?.[0];
    const firstTriggerKey = firstTrigger?.key;
    const firstTriggerType = firstTrigger?.type;

    // is weight alert
    if (firstTriggerKey === "weight" && firstTriggerType == "out-of-range")
      return true;

    // is blood pressure alert
    if (
      (firstTriggerKey === "diastolic" || firstTriggerKey === "systolic") &&
      firstTriggerType == "out-of-range"
    )
      return true;

    // is biological alert
    if (this.alertType === "biological") return true;

    // is a survey alert
    if (["rch", "crohn", "ibd-disk", "discharge-form"].includes(this.subType)) {
      return true;
    }

    return false;
  }

  get models() {
    const firstTrigger = this.triggers?.[0];
    const firstTriggerKey = firstTrigger?.key;
    const firstTriggerType = firstTrigger?.type;

    if (firstTriggerKey === "weight" && firstTriggerType == "out-of-range") {
      return { route: "dashboard.main.results-table.measurements" };
    } else if (
      (firstTriggerKey === "diastolic" || firstTriggerKey === "systolic") &&
      firstTriggerType == "out-of-range"
    ) {
      return { route: "blood-pressure.sessions" };
    }

    if (this.subType == "anxiety") {
      return { feature: "anxiety", component: "summary" };
    }

    if (this.subType == "rch") {
      return { feature: "mici", component: "rch-survey-summary" };
    }

    if (this.subType == "crohn") {
      return { feature: "mici", component: "crohn-survey-summary" };
    }

    if (this.subType == "ibd-disk") {
      return { feature: "mici", component: "ibd-disk-survey-summary" };
    }

    if (this.subType == "discharge-form") {
      return {
        feature: "diabetowise-discharge",
        component: "edition-form",
        sourceJeId: this.patient.diabetowiseDischargeCompletedJournalEvent?.id
      };
    }

    return { route: "dashboard.bio-results.summary" };
  }

  /**
   * The alert can target a JE that represents several values.
   * The code is extracted from the rawOmop value to designate the element that triggered the alert
   */
  get code(): string | undefined {
    return this.rawOmop ? this.rawOmop.variable : undefined;
  }

  /**
   * FIXME: this code should be moved out of the model. It should rather in the
   * component that needs this specific formatting, or in a helper if shared
   * between several components.
   *
   */
  override get description(): string {
    const { alertType, subType } = this;

    let title: string;
    switch (alertType) {
      case "clinical":
        if (subType && subType.length > 0) {
          title = this.intl.t(`alert.sub-type.${alertType}.${subType}`);
        } else {
          title = this.intl.t(`alert.alert-type.${alertType}`);
        }
        break;
      case "treatment-compliance":
        // Treatment-compliance alert are considered as journey alert.
        title = this.intl.t("alert.alert-type.journey");
        break;
      default:
        title = this.intl.t(`alert.alert-type.${alertType}`);
    }
    return title;
  }

  get isUnprocessed(): boolean {
    return this.alertProcessedJournalEvent == null;
  }

  get hasBeenProcessed(): boolean {
    return !this.isUnprocessed;
  }

  /** Is there an alertOngoingJournalEvent non archived attached to the alert */
  get isProcessing() {
    const ongoingJE = this.alertOngoingJournalEvent;
    return ongoingJE && !ongoingJE.isArchived;
  }

  /** Is this alert considered as "critical"? */
  get isCritical() {
    return this.criticality === "high";
  }

  /**
   * Process this alert.
   *
   * This creates a alert-processed-journal-event linked to this event, and sets
   * actionTaken attribute to the given parameter.
   */
  async processAlert({
    actionTaken,
    comment
  }: {
    actionTaken: string;
    comment?: string;
  }): Promise<AlertProcessedJournalEvent | false> {
    assert("actionTaken is mandatory", !!actionTaken);

    if (this.alertOngoingJournalEvent) {
      this.alertOngoingJournalEvent.unloadRecord();
    }

    const alertProcessedJournalEvent = this.store.createRecord(
      "alert-processed-journal-event",
      {
        patient: this.patient,
        occurredAt: new Date(),
        alertTriggeredJournalEvent: this.store.peekRecord(
          "alert-triggered-journal-event",
          this.id
        ),
        actionTaken,
        comment
      }
    );

    try {
      const processedAlert = await alertProcessedJournalEvent.save();

      await this.store.query("notification-processed-journal-event", {
        filter: {
          triggering_journal_event_id: this.triggeringJournalEventId
        }
      });
      return processedAlert;
    } catch {
      alertProcessedJournalEvent.unloadRecord();
      return false;
    }
  }

  get isTlsPrescriptionAlert(): boolean {
    return isTlsPrescriptionAlert(this);
  }

  get isBioExamPrescriptionAlert() {
    return isOfComputedType(this, "bio-exam-prescription");
  }

  get isMrcStageChangealert(): boolean {
    return isMrcStageChangealert(this);
  }

  get isMiciSurveyAlert(): boolean {
    return isOfComputedType(this, "mici-survey");
  }

  get isMedicationPrescriptionAlert(): boolean {
    return isOfComputedType(this, "medication-prescription");
  }

  get isTlsRenewal(): boolean {
    return (
      tlsPrescriptionTrigger(this).prescriptionType === "renewal" ||
      this.triggeringJournalEventType === "TlsPrescribedJournalEvent"
    );
  }

  get isJourneyForgetAlert(): boolean {
    return isJourneyForgetAlert(this);
  }

  prescribeRemoteMonitoringTask = task(async () => {
    if (this.isTlsPrescriptionAlert) {
      return this.prescribeTls();
    }
    throw new Error(`The alert '${this.id}' is not a remote monitoring alert`);
  });

  get nextPrescriptionStartDate(): Date {
    return new Date(tlsPrescriptionTrigger(this).nextPrescriptionStartDate);
  }

  get isolatedValues() {
    return this.rawOmop?.isolated_values;
  }

  async prescribeTls() {
    if (!this.isTlsPrescriptionAlert) {
      throw new Error(
        `Impossible to prescribe a TLS prescription for the non-tls alert '${this.id}'.`
      );
    }

    return this.store
      .createRecord("tls-prescribed-journal-event", {
        patient: this.patient,
        occurredAt: new Date(),
        startDate: this.nextPrescriptionStartDate,
        alertTriggeredJournalEvent: this
      })
      .save();
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "alert-triggered-journal-event": AlertTriggeredJournalEvent;
  }
}

declare module "semeia-api" {
  export interface ApiFilterRegistry {
    "alert-triggered-journal-event": {
      non_processed: "";
      for_current_medic?: "";
    };
  }
}

export type HomePageAlertType = Pick<
  AlertTriggeredJournalEvent,
  | "patient"
  | "id"
  | "triggeringJournalEventOccurredAt"
  | "triggeringJournalEventType"
  | "triggeringJournalEventId"
  | "frLocalizedDescription"
  | "occurredAt"
  | "commentWrittenJournalEvents"
  | "alertOngoingJournalEvent"
  | "alertType"
  | "source"
  | "subType"
  | "triggers"
  | "createdAt"
  | "isUnprocessed"
  | "processAlert"
>;
